import { useCallback, useRef, useState, type ReactNode } from 'react';

import { isStepUpRequired, type StepUpAction, type StepUpParams } from '../api/step-up.ts';
import { StepUpSheet } from '../components/step-up-sheet.tsx';
import { usePlatform } from '../platform/index.ts';

import { attemptConfirmation, type SheetSeed } from './step-up-attempt.ts';

export class StepUpCancelledError extends Error {
  constructor() {
    super('Confirmation cancelled');
    this.name = 'StepUpCancelledError';
  }
}

export interface GuardRequest {
  readonly action: StepUpAction;
  readonly params: StepUpParams;
  // One line describing the operation, shown on the confirmation sheet.
  readonly summary: string;
  readonly onSetUpBiometrics?: () => void;
}

interface PendingRequest extends GuardRequest {
  readonly seed: SheetSeed;
  readonly resolve: (grant: string) => void;
  readonly reject: (err: unknown) => void;
}

export interface StepUpGate {
  // Runs `call`, and if the bot refuses for want of a confirmation, asks for
  // one and runs it again with the grant. Anything else propagates untouched.
  readonly guard: <T>(request: GuardRequest, call: (grant?: string) => Promise<T>) => Promise<T>;
  readonly sheet: ReactNode;
}

export const useStepUpGate = (): StepUpGate => {
  const platform = usePlatform();
  const [pending, setPending] = useState<PendingRequest | null>(null);
  // Held in a ref as well so the sheet callbacks settle the promise that is
  // actually in flight, not one captured by a stale render.
  const pendingRef = useRef<PendingRequest | null>(null);

  const settle = useCallback((fn: (p: PendingRequest) => void): void => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    if (current !== null) fn(current);
  }, []);

  const guard = useCallback(
    async <T,>(request: GuardRequest, call: (grant?: string) => Promise<T>): Promise<T> => {
      try {
        return await call();
      } catch (err: unknown) {
        if (!isStepUpRequired(err)) throw err;
      }
      // A device that is already set up confirms without any UI of ours: it
      // shows the user its own prompt, naming the operation. The sheet appears
      // only when there is something to read or to decide.
      const attempt = await attemptConfirmation(
        request.action,
        request.params,
        request.summary,
        platform,
      );
      if (attempt.kind === 'GRANT') return call(attempt.grant);

      const grant = await new Promise<string>((resolve, reject) => {
        const next: PendingRequest = { ...request, seed: attempt.seed, resolve, reject };
        pendingRef.current = next;
        setPending(next);
      });
      return call(grant);
    },
    [platform],
  );

  const sheet =
    pending === null ? null : (
      <StepUpSheet
        action={pending.action}
        params={pending.params}
        summary={pending.summary}
        seed={pending.seed}
        {...(pending.onSetUpBiometrics !== undefined && {
          onSetUpBiometrics: pending.onSetUpBiometrics,
        })}
        onGranted={(grant): void => {
          settle((p) => {
            p.resolve(grant);
          });
        }}
        onCancel={(): void => {
          settle((p) => {
            p.reject(new StepUpCancelledError());
          });
        }}
      />
    );

  return { guard, sheet };
};

// True for the error the gate throws when the user closes the sheet. Screens
// use it to stay silent instead of showing a failure the user chose.
export const isStepUpCancelled = (err: unknown): boolean => err instanceof StepUpCancelledError;
